/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team
 * <mailto:radmanesh@gmail.com Arman Radmanesh>
 * <mailto:salarirad@gmail.com Mostafa Salari Rad>
 */
import { Fragment, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import PropTypes from 'prop-types';

// ─── Static demo data ─────────────────────────────────────────────────────────

const DEMO_PARTICIPANTS = [
  { id: 'p5xK2m', date: 'Dec 20', status: 'active',   days: 64, sensors: 9 },
  { id: 'p8rN4j', date: 'Dec 20', status: 'active',   days: 61, sensors: 7 },
  { id: 'p2wQ9a', date: 'Dec 20', status: 'active',   days: 58, sensors: 9 },
  { id: 'p7hL1b', date: 'Dec 20', status: 'partial',  days: 43, sensors: 5 },
  { id: 'p3tF6c', date: 'Dec 20', status: 'active',   days: 64, sensors: 8 },
  { id: 'p9mR3d', date: 'Dec 20', status: 'inactive', days: 12, sensors: 3 },
  { id: 'p1nX5e', date: 'Dec 20', status: 'active',   days: 60, sensors: 9 },
];

const SENSOR_DAYS = {
  p5xK2m: { 'Heart rate': 64, 'Sleep stages': 62, 'Steps': 64, 'HRV': 61, 'SpO2': 58, 'Temperature': 60, 'Breathing': 64 },
  p8rN4j: { 'Heart rate': 59, 'Sleep stages': 55, 'Steps': 61, 'HRV': 58, 'SpO2': 50, 'Temperature': 0,  'Breathing': 61 },
  p2wQ9a: { 'Heart rate': 58, 'Sleep stages': 56, 'Steps': 58, 'HRV': 55, 'SpO2': 53, 'Temperature': 54, 'Breathing': 58 },
  p7hL1b: { 'Heart rate': 40, 'Sleep stages': 38, 'Steps': 43, 'HRV': 0,  'SpO2': 0,  'Temperature': 0,  'Breathing': 43 },
  p3tF6c: { 'Heart rate': 63, 'Sleep stages': 60, 'Steps': 64, 'HRV': 60, 'SpO2': 58, 'Temperature': 55, 'Breathing': 64 },
  p9mR3d: { 'Heart rate': 10, 'Sleep stages': 8,  'Steps': 12, 'HRV': 0,  'SpO2': 0,  'Temperature': 0,  'Breathing': 12 },
  p1nX5e: { 'Heart rate': 60, 'Sleep stages': 58, 'Steps': 60, 'HRV': 57, 'SpO2': 54, 'Temperature': 56, 'Breathing': 60 },
};

const STREAM_COMPLETENESS = [
  { label: 'Steps & activity', pct: 100 },
  { label: 'Breathing rate',   pct: 100 },
  { label: 'Heart rate',       pct: 96  },
  { label: 'HRV intraday',     pct: 95  },
  { label: 'Sleep stages',     pct: 94  },
  { label: 'SpO2',             pct: 91  },
  { label: 'Skin temperature', pct: 84  },
];

const AVAILABLE_SENSORS = [
  { id: 'heart_rate',  label: 'Heart rate (intraday)' },
  { id: 'hrv',         label: 'Heart rate variability' },
  { id: 'sleep',       label: 'Sleep stages' },
  { id: 'steps',       label: 'Steps & activity' },
  { id: 'spo2',        label: 'SpO2 (blood oxygen)' },
  { id: 'temperature', label: 'Skin temperature' },
  { id: 'breathing',   label: 'Breathing rate' },
];

const DOWNLOAD_HISTORY = [
  { name: 'daily_averages_all_sensors_2025-04-30.csv', date: 'Apr 30, 2025', size: '2.1 MB' },
  { name: 'sleep_stages_detailed_2025-04-15.csv',      date: 'Apr 15, 2025', size: '4.3 MB' },
  { name: 'hrv_intraday_export_2025-03-01.json',       date: 'Mar 1, 2025',  size: '18.7 MB' },
];

const CHAT_RESPONSES = {
  'avg sleep by week':
    'Weekly avg across all 41 participants (Feb 25 to Apr 30):\n\nWk 1: 6.9h   Wk 2: 7.1h   Wk 3: 6.7h   Wk 4: 6.8h\nWk 5: 7.0h   Wk 6: 6.6h   Wk 7: 6.8h   Wk 8: 7.2h\n\nSlight dip in weeks 3 and 6. Break this down by sex or age group?',
  'resting HR over time':
    'Resting HR averaged 68.4 bpm across the study period, with a slight decline in weeks 6 to 8 (mean 66.1 bpm). Trend is consistent across age groups.',
  'steps by age group':
    'Age 25-34: 8,841 avg steps/day\nAge 35-44: 8,102 avg steps/day\nAge 45-54: 7,634 avg steps/day\n\nDifference is not statistically significant (p = 0.41) - sample size limits power here.',
  'HRV distribution':
    'RMSSD values range from 18-72 ms across participants.\nMedian: 34 ms. Distribution is right-skewed, typical for this age range.\n\nWant a histogram export?',
  'top 10 active participants':
    'Ranked by total data days:\n1. p5xK2m - 64d   2. p3tF6c - 64d   3. p8rN4j - 61d\n4. p1nX5e - 60d   5. p2wQ9a - 58d   6. p7hL1b - 43d\n7. p9mR3d - 12d',
};

const DEFAULT_BOT =
  "I've run that query against the dataset. Here are the results - want me to break it down by subgroup or export the table?";

const HOW_STEPS = [
  { num: '01', title: 'Post on Prolific',        body: 'Filter for existing device owners. No equipment purchase needed. ~25K eligible Fitbit users available now.' },
  { num: '02', title: 'Frictionless onboarding', body: 'Participants link their device via Google / OAuth in under 3 minutes. HIPAA-aware consent flow, no new accounts.' },
  { num: '03', title: 'Automated pipelines',     body: 'Periodic or on-demand fetching of HR, HRV, sleep, activity, SpO2, temperature - stored in a structured, queryable format.' },
  { num: '04', title: 'Dashboard + AI query',    body: 'Monitor adherence, visualise sensor streams, and query data in plain language. Export in CSV, JSON, or Parquet.' },
];

const TABS = ['Participants', 'Auto Fetch', 'Insights', 'AI Chat', 'Download', 'Download History'];

// ─── Micro components ─────────────────────────────────────────────────────────

function Sparkline({ points }) {
  const W = 120; const H = 36;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const pts = points
    .map((v, i) => `${(i / (points.length - 1)) * W},${H - 4 - ((v - min) / range) * (H - 8)}`)
    .join(' ');
  return (
    <svg className="jp-sparkline" viewBox={`0 0 ${W} ${H}`} height={H} aria-hidden="true">
      <polyline fill="none" stroke="#0A66C2" strokeWidth="1.5" points={pts} />
    </svg>
  );
}

Sparkline.propTypes = {
  points: PropTypes.arrayOf(PropTypes.number).isRequired,
};

function BarRow({ label, pct, val }) {
  return (
    <div className="jp-bar-row">
      <span className="jp-bar-label">{label}</span>
      <div className="jp-bar-track">
        <div className="jp-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="jp-bar-val">{val !== undefined ? val : `${pct}%`}</span>
    </div>
  );
}

BarRow.propTypes = {
  label: PropTypes.string.isRequired,
  pct: PropTypes.number.isRequired,
  val: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ─── Tab panels ───────────────────────────────────────────────────────────────

function TabParticipants() {
  const [selected, setSelected] = useState(null);
  const toggle = (id) => setSelected(prev => prev === id ? null : id);

  return (
    <>
      <p className="jp-muted" style={{ fontSize: 13, marginBottom: 14 }}>
        41 registered participants. Click any row to inspect sensor coverage.
      </p>
      <div className="jp-table-wrap">
        <table className="jp-table">
          <thead>
            <tr>
              {['Participant ID', 'Registered', 'Status', 'Data days', 'Sensors', ''].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DEMO_PARTICIPANTS.map(p => (
              <tr
                key={p.id}
                className={selected === p.id ? 'selected' : ''}
                onClick={() => toggle(p.id)}
              >
                <td className="jp-mono" style={{ fontSize: 12 }}>{p.id}</td>
                <td className="jp-muted" style={{ fontSize: 13 }}>{p.date}</td>
                <td>
                  <span className={`jp-chip jp-chip-${p.status}`}>{p.status}</span>
                </td>
                <td className="jp-mono">{p.days}</td>
                <td className="jp-mono">{p.sensors} / 9</td>
                <td>
                  <button
                    className="jp-btn jp-btn-ghost"
                    onClick={e => { e.stopPropagation(); toggle(p.id); }}
                  >
                    inspect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && SENSOR_DAYS[selected] && (
        <div className="jp-detail-box">
          <span className="jp-detail-label">sensor coverage — participant {selected}</span>
          {Object.entries(SENSOR_DAYS[selected]).map(([k, v]) => (
            <BarRow key={k} label={k} pct={Math.round((v / 64) * 100)} val={`${v}d`} />
          ))}
        </div>
      )}
    </>
  );
}

function TabAutoFetch() {
  const [added, setAdded] = useState([]);
  const [freqs, setFreqs] = useState({});

  const handleAdd = (e) => {
    const val = e.target.value;
    if (!val || added.find(s => s.id === val)) { e.target.value = ''; return; }
    const sensor = AVAILABLE_SENSORS.find(s => s.id === val);
    if (!sensor) return;
    setAdded(prev => [...prev, sensor]);
    setFreqs(prev => ({ ...prev, [val]: 'daily' }));
    e.target.value = '';
  };

  const remaining = AVAILABLE_SENSORS.filter(s => !added.find(a => a.id === s.id));

  return (
    <>
      <p className="jp-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Select sensors to add. Each sensor gets its own fetch schedule configuration.
      </p>
      <div style={{ maxWidth: 440 }}>
        <div className="jp-field">
          <label>Add sensor stream</label>
          <select className="jp-select" onChange={handleAdd} defaultValue="">
            <option value="" disabled>— choose a sensor —</option>
            {remaining.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {added.length === 0 && (
          <p className="jp-muted" style={{ fontSize: 13 }}>No sensors added yet.</p>
        )}

        {added.map(s => (
          <div className="jp-fetch-spec" key={s.id}>
            <span className="jp-fetch-spec-name">{s.label}</span>
            <select
              className="jp-freq-select"
              value={freqs[s.id] || 'daily'}
              onChange={e => setFreqs(prev => ({ ...prev, [s.id]: e.target.value }))}
            >
              <option value="hourly">hourly</option>
              <option value="daily">daily</option>
              <option value="on-demand">on-demand</option>
            </select>
          </div>
        ))}
      </div>
    </>
  );
}

function TabInsights() {
  return (
    <>
      <p className="jp-muted" style={{ fontSize: 13, marginBottom: 18 }}>
        Group-level descriptive summaries from the pilot study (N = 41, Feb 25 to Apr 30 2025).
      </p>
      <div className="jp-insight-grid">
        {[
          { num: '68.4',  label: 'Avg resting HR (bpm)',  pts: [72, 70, 69, 65, 68, 64, 66] },
          { num: '6.8h',  label: 'Avg nightly sleep',     pts: [65, 71, 66, 70, 64, 72, 67] },
          { num: '7,842', label: 'Avg daily steps',       pts: [72, 81, 75, 84, 70, 82, 78] },
          { num: '96.1%', label: 'Avg SpO2',              pts: [96, 97, 96, 97, 96, 97, 96] },
        ].map(c => (
          <div className="jp-insight-card" key={c.label}>
            <div className="jp-insight-num">{c.num}</div>
            <div className="jp-insight-lbl">{c.label}</div>
            <Sparkline points={c.pts} />
          </div>
        ))}
      </div>

      <p className="jp-label" style={{ marginTop: 8 }}>Data completeness by stream</p>
      {STREAM_COMPLETENESS.map(s => (
        <BarRow key={s.label} label={s.label} pct={s.pct} />
      ))}
    </>
  );
}

function TabChat() {
  const [messages, setMessages] = useState([
    { role: 'user', text: 'Show me average nightly sleep duration by week' },
    { role: 'bot',  text: CHAT_RESPONSES['avg sleep by week'] },
  ]);
  const [input, setInput] = useState('');
  const threadRef = useRef(null);

  const send = (text) => {
    const q = (text || input).trim();
    if (!q) return;
    const reply = CHAT_RESPONSES[q] || DEFAULT_BOT;
    setMessages(prev => [...prev, { role: 'user', text: q }, { role: 'bot', text: reply }]);
    setInput('');
    setTimeout(() => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, 40);
  };

  return (
    <>
      <p className="jp-muted" style={{ fontSize: 13, marginBottom: 14 }}>
        Query your dataset in plain language. Jamasp translates requests into database queries.
      </p>
      <div className="jp-sample-queries">
        {Object.keys(CHAT_RESPONSES).map(q => (
          <button key={q} className="jp-sample-q" onClick={() => send(q)}>{q}</button>
        ))}
      </div>
      <div className="jp-chat-thread" ref={threadRef}>
        {messages.map((m, i) => (
          <div key={i} className={`jp-chat-msg jp-chat-${m.role}`}>{m.text}</div>
        ))}
      </div>
      <div className="jp-chat-input-row">
        <input
          className="jp-input"
          placeholder="ask something about your data..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
        />
        <button className="jp-btn jp-btn-primary" onClick={() => send()}>Send</button>
      </div>
    </>
  );
}

function TabDownload() {
  return (
    <>
      <p className="jp-muted" style={{ fontSize: 13, marginBottom: 18 }}>
        Export analysis-ready data. Choose a predefined configuration or build a custom export.
      </p>
      <div style={{ maxWidth: 440 }}>
        <div className="jp-field">
          <label>Data format</label>
          <select className="jp-select">
            <option>CSV (long format)</option>
            <option>CSV (wide format)</option>
            <option>JSON</option>
            <option>Parquet</option>
          </select>
        </div>
        <div className="jp-field">
          <label>Preset configuration</label>
          <select className="jp-select">
            <option>Daily averages — all sensors</option>
            <option>Intraday steps only</option>
            <option>Sleep stages (detailed)</option>
            <option>HRV indices</option>
            <option>Custom...</option>
          </select>
        </div>
        <div className="jp-field">
          <label>Date range</label>
          <select className="jp-select">
            <option>Feb 25 – Apr 30, 2025</option>
            <option>Custom range...</option>
          </select>
        </div>
        <button className="jp-btn jp-btn-primary" style={{ marginTop: 4 }}>
          Prepare export
        </button>
      </div>
    </>
  );
}

function TabDownloadHistory() {
  return (
    <>
      <p className="jp-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Previously prepared exports, available for re-download.
      </p>
      {DOWNLOAD_HISTORY.map(f => (
        <div className="jp-dl-file" key={f.name}>
          <div>
            <div className="jp-dl-file-name">{f.name}</div>
            <div className="jp-dl-meta">{f.date} · {f.size}</div>
          </div>
          <button className="jp-btn jp-btn-ghost">Re-download</button>
        </div>
      ))}
    </>
  );
}

const TAB_CONTENT = [
  TabParticipants,
  TabAutoFetch,
  TabInsights,
  TabChat,
  TabDownload,
  TabDownloadHistory,
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" style={{flexShrink:0}}>
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
    <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
  </svg>
);

function AccessModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await fetch('https://formspree.io/f/xgodjvgb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, message: note || '(no note)' }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true); // still show success; formspree is reliable
    }
  };

  return (
    <div
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}
      onClick={onClose}
    >
      <div
        style={{background:'#fff',borderRadius:12,padding:'36px 32px',width:'100%',maxWidth:420,boxShadow:'0 8px 40px rgba(0,0,0,0.12)',position:'relative'}}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{position:'absolute',top:14,right:16,background:'none',border:'none',fontSize:20,color:'#94A6B8',cursor:'pointer',lineHeight:1}}
        >×</button>

        <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:'1.5px',textTransform:'uppercase',color:'#0A66C2',display:'block',marginBottom:8}}>Research access</span>
        <h2 style={{fontSize:19,fontWeight:700,color:'#131B20',marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>Welcome to Jamasp</h2>
        <p style={{fontSize:13,color:'#6B7C8D',marginBottom:24,fontFamily:"'DM Sans',sans-serif",lineHeight:1.6}}>A platform for full-cycle wearable research.</p>

        {/* Returning users */}
        <div style={{fontSize:13,fontWeight:600,color:'#131B20',marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>Already have access?</div>
        <a
          href="/auth/login"
          style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,width:'100%',padding:'10px 16px',border:'1px solid #D6E2EB',borderRadius:8,background:'#fff',fontSize:13,fontWeight:500,color:'#131B20',fontFamily:"'DM Sans',sans-serif",textDecoration:'none',boxSizing:'border-box',marginBottom:24}}
        >
          <GoogleIcon />
          Sign in with Google
        </a>

        {/* Divider */}
        <div style={{borderTop:'1px solid #EAF0F5',position:'relative',marginBottom:24}}>
          <span style={{position:'absolute',top:-9,left:'50%',transform:'translateX(-50%)',background:'#fff',padding:'0 12px',fontSize:11,color:'#94A6B8',fontFamily:"'DM Sans',sans-serif"}}>or</span>
        </div>

        {/* New users */}
        <div style={{fontSize:13,fontWeight:600,color:'#131B20',marginBottom:6,fontFamily:"'DM Sans',sans-serif"}}>New to Jamasp?</div>
        <p style={{fontSize:12,color:'#6B7C8D',marginBottom:14,fontFamily:"'DM Sans',sans-serif",lineHeight:1.6}}>
          Jamasp is available to research teams by invitation. Let us know and we&apos;ll be in touch.
        </p>

        {submitted ? (
          <div style={{background:'#EDF7EF',border:'1px solid #B8DFC0',borderRadius:8,padding:'12px 16px',fontSize:13,color:'#1a6b2a',fontFamily:"'DM Sans',sans-serif",lineHeight:1.5}}>
            ✓ Request sent — we&apos;ll follow up at <strong>{email}</strong> shortly.
          </div>
        ) : (
          <form onSubmit={handleRequest}>
            <input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{width:'100%',padding:'9px 12px',border:'1px solid #D6E2EB',borderRadius:8,fontSize:13,fontFamily:"'DM Sans',sans-serif",color:'#131B20',marginBottom:10,boxSizing:'border-box',outline:'none'}}
            />
            <textarea
              placeholder="Optional: briefly describe your study or interest"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              style={{width:'100%',padding:'9px 12px',border:'1px solid #D6E2EB',borderRadius:8,fontSize:13,fontFamily:"'DM Sans',sans-serif",color:'#131B20',marginBottom:12,boxSizing:'border-box',outline:'none',resize:'vertical'}}
            />
            <button
              type="submit"
              style={{width:'100%',padding:'10px 16px',background:'#0A66C2',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif",cursor:'pointer'}}
            >
              Request access
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

AccessModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default function Home() {
  const [activeTab, setActiveTab] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const ActivePanel = TAB_CONTENT[activeTab];

  return (
    <Fragment>
      <div className="jp-root">

        {/* Hero */}
        <section className="jp-hero">
          <span className="jp-badge">open research infrastructure</span>
          <h1 className="jp-hero-h1">
            Wearable data collection,<br />
            <strong>built for behavioral science.</strong>
          </h1>
          <p className="jp-hero-sub">
            Jamasp connects commercial wearables with participant recruitment platforms,
            automates data pipelines, and gives researchers analysis-ready physiological
            data — without the engineering overhead.
          </p>
          <div className="jp-hero-btns">
            <button onClick={() => setShowModal(true)} className="jp-btn jp-btn-primary">Request access</button>
            <a href="https://drive.google.com/file/d/14NWO8hd75UVm5tAxGujvHOcY9uBvWQ26/" target="_blank" rel="noreferrer" className="jp-btn jp-btn-outline">Read the paper</a>
          </div>
        </section>

        {/* Stats */}


        {/* How it works */}
        <section className="jp-section">
          <span className="jp-label">how it works</span>
          <h2 className="jp-section-title">
            Recruit. Consent.<br />
            <strong>Collect. Analyze.</strong>
          </h2>
          <p className="jp-section-sub">
            Jamasp handles the full lifecycle of a wearable study — from posting on
            Prolific to analysis-ready data — in a single integrated platform.
          </p>
          <div className="jp-steps">
            {HOW_STEPS.map(s => (
              <div className="jp-step" key={s.num}>
                <div className="jp-step-num">{s.num}</div>
                <div className="jp-step-title">{s.title}</div>
                <div className="jp-step-body">{s.body}</div>
              </div>
            ))}
          </div>
        </section>



        {/* Explore pilot data */}
        <section className="jp-section" style={{ paddingTop: 48, paddingBottom: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <span className="jp-label">Pilot study · open data</span>
              <h2 className="jp-section-title" style={{ marginBottom: 10 }}>Explore real <strong>wearable data</strong></h2>
              <p className="jp-section-sub" style={{ margin: 0 }}>
                Browse anonymized physiological data from 41 Fitbit owners recruited via Prolific —
                hourly step counts, sleep stages, and wear-time coverage from the Jamasp pilot study.
              </p>
            </div>
            <div style={{ flexShrink: 0 }}>
              <Link to="/explore" className="jp-btn jp-btn-primary">
                Explore pilot data →
              </Link>
            </div>
          </div>
        </section>
      
        {/* Pilot study stats */}
        <div className="jp-section" style={{ paddingTop: 0 }}> {/* This keeps it aligned with the text above it */}
          <div className="jp-steps">
            {[
              { num: 'N = 41',  label: 'pilot participants' },
              { num: '64',      label: 'avg. days of data' },
              { num: '2.6 min', label: 'median registration time' },
              { num: '9',       label: 'sensor streams' },
            ].map(s => (
              <div className="jp-step" key={s.label} style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div className="jp-stat-num">{s.num}</div>
                <div className="jp-step-title" style={{ margin: 0, fontWeight: 400, color: 'var(--jp-gray-600)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard demo */}
        <section className="jp-section">
          <span className="jp-label">dashboard</span>
          <h2 className="jp-section-title">
            Everything in one place.<br />
            <strong>From recruit to result.</strong>
          </h2>
          <p className="jp-section-sub">
            Browse the researcher dashboard below. This is a view-only demo —
            in the live platform all actions persist to your project.
          </p>

          <div className="jp-demo">
            <div className="jp-tab-bar" role="tablist">
              {TABS.map((t, i) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={activeTab === i}
                  className={`jp-tab${activeTab === i ? ' active' : ''}`}
                  onClick={() => setActiveTab(i)}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="jp-panel active" role="tabpanel">
              <ActivePanel />
            </div>
          </div>
        </section>




        {/* Research */}
        <section className="jp-section">
          <span className="jp-label">research</span>
          <h2 className="jp-section-title">
            Pilot-tested.<br />
          </h2>
          <p className="jp-section-sub">
            Jamasp was developed and validated in a pilot study recruiting 41 Fitbit owners
            via Prolific, collecting over 64 days of physiological data per participant.
          </p>
          <div className="jp-paper-cite">
            <div className="jp-paper-cite-label">preprint</div>
            <div className="jp-paper-cite-title">
              Wearable Device Data Collection From Existing Device Owners via Crowdsourcing Platforms: A Feasibility Study Using Jamasp
            </div>
            <div className="jp-paper-cite-authors">
              Mostafa Salari Rad &middot; Arman Radmanesh &middot;
              New School for Social Research &amp; University of Oklahoma
            </div>
          </div>

        </section>

        {/* CTA */}
        <div className="jp-cta">
          <h2>
            Ready to run your study?<br />
            <strong>Jamasp handles the infrastructure.</strong>
          </h2>
          <p>
            Request access to the platform or reach out to learn how Jamasp
            can support your next wearable research project.
          </p>
          <div className="jp-cta-btns">
            <button onClick={() => setShowModal(true)} className="jp-btn jp-btn-primary" style={{background:'#fff',color:'#0A66C2'}}>
              Request access
            </button>
            <a
              href="https://github.com/cutsocial/jamasp-public"
              target="_blank"
              rel="noreferrer"
              className="jp-btn jp-btn-outline"
              style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.08)' }}
            >
              View code &amp; data
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className="jp-footer">
          <nav className="jp-footer-links">
            <Link to="/about">About</Link>
            <a href="https://github.com/cutsocial/jamasp-public" target="_blank" rel="noreferrer">GitHub</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href="mailto:salarirad@newschool.edu">Contact</a>
          </nav>
          <span className="jp-footer-copy">© Jamasp 2026</span>
        </footer>

      </div>
      {showModal && <AccessModal onClose={() => setShowModal(false)} />}
    </Fragment>
  );
}
